/* eslint-disable max-lines -- nullableStringLocaleOrder 廃止に伴う inline 展開で 450 行超過。本ファイルの refactor は別 MR */
import { EditOutlined, ExclamationCircleOutlined, MailOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { useMutation, useSuspenseQuery } from '@apollo/client'
import {
  FetchMeDocument,
  FetchMixedUsersDocument,
  type FetchMixedUsersQuery,
  FetchUserGroupsDocument,
  type IdentityProviderEnum,
  UpsertInvitationsDocument,
  UpsertUsersDocument,
  type UserGroup,
} from '@salescore/client-api'
import {
  canUploadImage,
  getOrganizationIdFromPath,
  HandleQuery,
  toUserPlansInDisplayOrder,
  UploadImageButton,
  useCan,
  UserAvatar,
  UserPlanTag,
} from '@salescore/client-common'
import { BUILTIN_USER_ROLES, type BuiltInUserRole } from '@salescore/features'
// eslint-disable-next-line sonarjs/deprecation
import { createDestroyColumn, getColumnSearchProps, organizationHasFeature, useModal } from '@salescore/frontend-common'
import { Button, message, Select, Table, Tag, Tooltip } from 'antd'
import { Array, Order, pipe, String } from 'effect'
import { isNotNullish, isNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { type ReactNode, useMemo, useState, useTransition } from 'react'

import { useCreateUserRoleOptions, USER_PROVIDER_JA, USER_ROLE_JA } from '../invitations/CreateInvitationForm'
import { AssignmentHistoryModal } from './AssignmentHistoryModal'
import { INTEGRATION_PROVIDERS_WITH_COLUMN_NAMES, IntegrationProviderColumnIcon } from './IntegrationProviderColumnIcon'

type MixedUser = FetchMixedUsersQuery['mixedUsers'][0]
// グループ名は表示用に付与するので、Table の行型は MixedUser そのものではない
type MixedUserRow = MixedUser & { userGroupName: string }

export const roleToRank = (role?: string) => {
  if (role === undefined) {
    return 9
  }

  if (role.startsWith('custom_')) {
    return 6
  }

  return (
    {
      [BUILTIN_USER_ROLES.admin]: 0,
      [BUILTIN_USER_ROLES.manager]: 1,
      [BUILTIN_USER_ROLES.playingManager]: 2,
      [BUILTIN_USER_ROLES.nxMember]: 3,
      [BUILTIN_USER_ROLES.member]: 4,
      [BUILTIN_USER_ROLES.minimal]: 5,
      [BUILTIN_USER_ROLES.guest]: 10,
    }[role] ?? 100
  )
}

// role は custom_xxx を含む素の string なので、USER_ROLE_JA のキーに無い値も引ける形で受ける
const USER_ROLE_LABELS: Record<string, string | undefined> = USER_ROLE_JA

export const roleLabel = (role: string | undefined): string => {
  if (role === undefined) {
    return ''
  }
  // eslint-disable-next-line security/detect-object-injection -- 表示ラベルの解決にのみ使う
  return USER_ROLE_LABELS[role] ?? role
}

// USER_PROVIDER_JA は IdentityProviderEnum を網羅する Record だが、値は t() 経由なので i18next の初期化前は
// 型に反して undefined になる。sorter がその値を localeCompare に渡すため、フォールバックを残す
export const providerLabel = (provider: IdentityProviderEnum): string =>
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, security/detect-object-injection -- 上記のとおり実行時は undefined になりうる
  USER_PROVIDER_JA[provider] ?? provider

/**
 * 権限カラムの sorter。
 *
 * raw な role 値の辞書順ではなく roleToRank で権限の強い順に並べ、同 rank 内 (custom 権限は全て rank=6) は
 * 表示ラベルで並べる。既定の行並びは user.role のみを見るので、招待のみの行の位置はこの列のソートとは一致しない。
 */
export const compareUsersByRole = Order.mapInput(
  Order.Tuple([Order.Number, Order.make<string>((a, b) => String.localeCompare(b)(a))]),
  (x: { user?: { role: string } | null; invitation?: { role: string } | null }) => {
    const role = x.user?.role ?? x.invitation?.role
    return [roleToRank(role), roleLabel(role)] as const
  },
)

/** ログイン方法カラムの sorter。表示ラベルで並べ、user も invitation も無い行は末尾に置く */
export const compareUsersByProvider = Order.mapInput(
  Order.make<string | undefined>((a, b) =>
    // eslint-disable-next-line sonarjs/no-nested-conditional
    isNotNullish(a) ? (isNotNullish(b) ? String.localeCompare(b)(a) : -1) : isNotNullish(b) ? 1 : 0,
  ),
  (x: {
    user?: { identity: { provider: IdentityProviderEnum } } | null
    invitation?: { provider: IdentityProviderEnum } | null
  }) => {
    const provider = x.user?.identity.provider ?? x.invitation?.provider
    return provider === undefined ? provider : providerLabel(provider)
  },
)

export const UsersTable = (): ReactNode => {
  const options = useCreateUserRoleOptions()
  const { data, refetch } = useSuspenseQuery(FetchMixedUsersDocument, {
    variables: { organizationId: getOrganizationIdFromPath() },
  })
  const getUserGroups = useSuspenseQuery(FetchUserGroupsDocument, {
    variables: { organizationId: getOrganizationIdFromPath() },
  })
  const users = data.mixedUsers
  const [fetching, startTransition] = useTransition()
  const invalidate = () => {
    startTransition(() => {
      void refetch()
      void getUserGroups.refetch()
    })
  }

  /** グループID に対してのグループ名の Map */
  const userGroupIdToName = useMemo(() => {
    // groupId に対しての Groupオブジェクトの Map
    const map = new Map(getUserGroups.data.userGroups.map((x) => [x.id, x]))

    // グループID に対しての"親グループ/子グループ"形式のラベルをセット
    return new Map<string, string>(
      getUserGroups.data.userGroups.map((group) => [
        group.id,
        getGroupNames(map, group.id, [], new Set<string>()).join(' / '),
      ]),
    )
  }, [getUserGroups.data.userGroups])

  // eslint-disable-next-line sonarjs/todo-tag
  // const me = api.fetchMe({ organizationId: getOrganizationIdFromPath() }) // TODO
  const [upsertInvitationsMutation] = useMutation(UpsertInvitationsDocument)
  const [upsertUsersMutation] = useMutation(UpsertUsersDocument)
  // const [updateResourceUsersMutation] = useUpdateResourceUsersMutation()
  const [updatingUserIds, setUpdatingUserIds] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const sortedUsers = pipe(
    users,
    Array.map((x) => {
      const userGroupId = x.resourceUser.user_group_id
      return { ...x, userGroupName: isNotNullish(userGroupId) ? (userGroupIdToName.get(userGroupId) ?? '') : '' }
    }),
    Array.filter((x) => x.user?.role !== BUILTIN_USER_ROLES.admin),
    Array.sortBy(
      Order.mapInput(
        Order.Tuple([
          Order.Number,
          Order.make<string | null | undefined>((a, b) =>
            a === b
              ? 0
              : // eslint-disable-next-line sonarjs/no-nested-conditional
                a === null || a === undefined
                ? -1
                : // eslint-disable-next-line sonarjs/no-nested-conditional
                  b === null || b === undefined
                  ? 1
                  : String.localeCompare(b)(a),
          ),
          Order.String,
        ]),
        (x) => [roleToRank(x.user?.role), x.invitation?.role ?? null, x.resourceUser.email] as const,
      ),
    ),
  )
  const canManageUsers = useCan('manage-users')
  const canManageAssignmentHistory = useCan('manage-group-assignment-history')
  // eslint-disable-next-line sonarjs/deprecation
  const assignmentHistoryModal = useModal<MixedUser>()

  const organizationId = getOrganizationIdFromPath()
  const {
    data: { myUser },
  } = useSuspenseQuery(FetchMeDocument, {
    variables: { organizationId },
    fetchPolicy: 'cache-first',
  })
  const { identity: myIdentity, organization } = myUser
  const me = {
    myUser,
    myIdentity,
    organization,
  }
  const hasAssignmentHistoryFeature = organizationHasFeature(organization, 'enable_group_assignment_history')
  return (
    <>
      <HandleQuery>
        <Table<MixedUserRow>
          loading={fetching}
          rowKey="id"
          size="small"
          pagination={{ defaultPageSize: 100 }}
          dataSource={sortedUsers}
          columns={[
            {
              title: t(`ユーザー名`),
              dataIndex: 'name',
              width: 200,
              sorter: Order.mapInput(
                Order.make<string>((a, b) => String.localeCompare(b)(a)),
                (x: MixedUser) => x.resourceUser.name,
              ),
              ...getColumnSearchProps((record: MixedUser) => record.resourceUser.name),
              render(_text, record: MixedUser) {
                return (
                  <Tooltip title={`${t(`ユーザーID`)}: ${record.id}`}>
                    <span>
                      <UserAvatar name={record.resourceUser.name} imageUrl={record.resourceUser.image_url ?? ''} />{' '}
                      {record.resourceUser.name}
                    </span>
                  </Tooltip>
                )
              },
            },
            {
              title: t(`メールアドレス`),
              dataIndex: 'email',
              sorter: Order.mapInput(
                Order.make<string>((a, b) => String.localeCompare(b)(a)),
                (x: MixedUser) => x.resourceUser.email,
              ),
              ...getColumnSearchProps((record: MixedUser) => record.resourceUser.email),
              render(_text, record: MixedUser) {
                return <span>{record.resourceUser.email}</span>
              },
            },
            {
              title: t('グループ名'),
              dataIndex: 'user_group_id',
              width: 180,
              sorter: Order.mapInput(
                Order.make<string>((a, b) => String.localeCompare(b)(a)),
                (x: MixedUserRow) => x.userGroupName,
              ),
              ...getColumnSearchProps<{ userGroupName: string }>(({ userGroupName }) => userGroupName),
              render: (_text, user): React.ReactNode => <span>{user.userGroupName}</span>,
            },
            ...(hasAssignmentHistoryFeature && canManageAssignmentHistory
              ? [
                  {
                    title: t('所属履歴'),
                    width: 100,
                    render(_: unknown, record: MixedUser) {
                      return (
                        <Button
                          icon={<EditOutlined />}
                          onClick={() => {
                            assignmentHistoryModal.showModal(record)
                          }}
                        >
                          {t('履歴')}
                        </Button>
                      )
                    },
                  },
                ]
              : []),
            {
              title: t(`連携状況`),
              dataIndex: 'integration',
              width: 100,
              render(_text, record: MixedUser) {
                return (
                  <div key={`${record.id}-integrations`}>
                    {INTEGRATION_PROVIDERS_WITH_COLUMN_NAMES.map((provider) => {
                      // カラム名とIDのペア（IDがnullやundefinedでもツールチップ上にラベル表示する）
                      const columnNameAndIds = provider.columns.map((column) => ({
                        columnName: column,
                        id: record.resourceUser[column],
                      }))

                      // 表示できる項目が無い場合はアイコンを表示しない
                      if (columnNameAndIds.every(({ id }) => isNullish(id))) {
                        // eslint-disable-next-line @eslint-react/no-missing-key
                        return <></>
                      }
                      return (
                        <IntegrationProviderColumnIcon
                          key={`${record.id}-${provider.provider}`}
                          provider={provider}
                          columnNameAndIds={columnNameAndIds}
                        />
                      )
                    })}
                  </div>
                )
              },
            },
            {
              title: t(`招待`),
              width: 70,
              dataIndex: 'invitationStatus',
              sorter: Order.mapInput(
                Order.Boolean,
                (x: MixedUser) => (x.user === undefined || x.user === null) && isNotNullish(x.invitation),
              ),
              render(_text, record: MixedUser) {
                const { user, invitation } = record
                if ((user === undefined || user === null) && isNotNullish(invitation)) {
                  return <Tag icon={<MailOutlined />}>{t(`招待中`)}</Tag>
                }
                return <span></span>
              },
            },
            {
              title: t(`ライセンス`),
              width: 100,
              // UserPlanTag がタグを出す順 (SYNC → VISUALIZE) で比較する。
              // user と invitation でプランの取得元が違うので、表示と同じ導出関数を共有する
              sorter: Order.mapInput(
                Order.Array(
                  Order.make<string | null | undefined>((a, b) =>
                    // eslint-disable-next-line sonarjs/no-nested-conditional
                    isNotNullish(a) ? (isNotNullish(b) ? String.localeCompare(b)(a) : -1) : isNotNullish(b) ? 1 : 0,
                  ),
                ),
                (x: MixedUser) => toUserPlansInDisplayOrder(x.user ?? x.invitation ?? undefined),
              ),
              render(_, record) {
                return <UserPlanTag userOrInvitation={record.user ?? record.invitation ?? undefined} />
              },
            },
            {
              title: t(`権限`),
              width: 180,

              sorter: compareUsersByRole,

              render(_, record) {
                const role = record.user?.role ?? record.invitation?.role
                if (role === undefined) {
                  // eslint-disable-next-line sonarjs/todo-tag
                  // TODO
                  return (
                    <Tooltip
                      title={t(
                        `連携中のCRMに存在するユーザーです。SALESCOREのアカウントを持たないため、請求対象にはなりません。`,
                      )}
                    >
                      <div className="text-gray-600">
                        {t(`連携先ユーザー`)} <QuestionCircleOutlined />
                      </div>
                    </Tooltip>
                  )
                }

                return (
                  <Select
                    style={{ width: 200 }}
                    className="w-full"
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                    value={{ label: USER_ROLE_JA[role as BuiltInUserRole], value: role }}
                    labelInValue
                    options={options.filter((x) => x.value !== BUILTIN_USER_ROLES.minimal)}
                    optionRender={(option) => <span className="whitespace-normal break-words">{option.label}</span>}
                    loading={updatingUserIds.includes(record.id)}
                    disabled={
                      (me.myUser.role !== BUILTIN_USER_ROLES.admin && me.myUser.role !== BUILTIN_USER_ROLES.manager) ||
                      updatingUserIds.includes(record.id) ||
                      !canManageUsers ||
                      record.user?.role === BUILTIN_USER_ROLES.minimal // Minimalはadmin画面でのみ設定可能
                    }
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
                    onChange={async (e: { label: string; value: string }) => {
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                      const role = e.value as BuiltInUserRole
                      const { invitation, user } = record
                      setUpdatingUserIds((old) => [...old, record.id])

                      if (isNotNullish(user)) {
                        await upsertUsersMutation({
                          variables: {
                            organizationId: getOrganizationIdFromPath(),
                            users: [
                              {
                                id: user.id,
                                role,
                              },
                            ],
                          },
                        })
                      } else if (isNotNullish(invitation)) {
                        await upsertInvitationsMutation({
                          variables: {
                            organizationId: getOrganizationIdFromPath(),
                            invitations: [
                              {
                                id: invitation.id,
                                email: invitation.email,
                                provider: invitation.provider,
                                role,
                              },
                            ],
                          },
                        })
                      }

                      setUpdatingUserIds((old) => old.filter((x) => x !== record.id))
                      invalidate()
                      void message.success(t(`役割を更新しました`))
                    }}
                  />
                )
              },
            },
            {
              title: t(`ログイン方法`),
              width: 200,

              sorter: compareUsersByProvider,

              render(_, record: MixedUser) {
                const provider = record.user?.identity.provider ?? record.invitation?.provider
                if (provider === undefined) {
                  // eslint-disable-next-line sonarjs/todo-tag
                  // TODO
                  return <div className="text-gray-500">-</div>
                }

                return <div className="">{USER_PROVIDER_JA[provider]}</div>
              },
            },
            ...(canUploadImage(me)
              ? [
                  {
                    title: (
                      <span>
                        <ExclamationCircleOutlined className="mr-1" />
                        {t(`画像アップロード`)}
                      </span>
                    ),
                    render(_: unknown, record: MixedUser) {
                      const { user } = record
                      return (
                        <div key={record.id}>
                          {user === undefined || user === null ? null : (
                            <UploadImageButton
                              // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
                              onFinish={async (imageUrl) => {
                                setUpdatingUserIds((old) => [...old, record.id])
                                await upsertUsersMutation({
                                  variables: {
                                    organizationId: getOrganizationIdFromPath(),
                                    users: [
                                      {
                                        id: user.id,
                                        imageUrl,
                                      },
                                    ],
                                  },
                                })
                                invalidate()
                                setUpdatingUserIds((old) => old.filter((x) => x !== record.id))
                                void message.success(t(`プロフィール画像を更新しました`))
                              }}
                            />
                          )}
                        </div>
                      )
                    },
                  },
                ]
              : []),
            ...(me.myUser.role === BUILTIN_USER_ROLES.admin || me.myUser.role === BUILTIN_USER_ROLES.manager
              ? [
                  createDestroyColumn(
                    loading,
                    setLoading,
                    async (mixedUser: MixedUser) => {
                      const { user, invitation } = mixedUser
                      if (isNotNullish(user)) {
                        await upsertUsersMutation({
                          variables: {
                            organizationId: getOrganizationIdFromPath(),
                            users: [
                              {
                                id: user.id,
                                deleted: true,
                              },
                            ],
                          },
                        })
                      } else if (isNotNullish(invitation)) {
                        await upsertInvitationsMutation({
                          variables: {
                            organizationId: getOrganizationIdFromPath(),
                            invitations: [
                              {
                                id: invitation.id,
                                email: invitation.email,
                                provider: invitation.provider,
                                role: invitation.role,
                                deleted: true,
                              },
                            ],
                          },
                        })
                      }
                      invalidate()
                    },
                    (mixedUser: MixedUser) => {
                      if (!canManageUsers) {
                        return false
                      }
                      const { user, invitation } = mixedUser
                      return isNotNullish(user) || isNotNullish(invitation)
                    },
                  ),
                ]
              : []),
          ]}
        />
      </HandleQuery>
      <AssignmentHistoryModal modal={assignmentHistoryModal} />
    </>
  )
}

/** "user_group_id"から、全階層のグループ名の配列を取得 */
const getGroupNames = (
  groupKeyValue: Map<string, UserGroup>,
  groupId: string,
  groupNames: string[],
  visitedIds: Set<string>,
): string[] => {
  if (visitedIds.has(groupId)) {
    return groupNames // 循環参照を検出した場合は現在のラベルを返す
  }

  const group = groupKeyValue.get(groupId)
  if (!isNotNullish(group)) {
    return groupNames
  }
  visitedIds.add(groupId)
  const newGroupNames = [group.name, ...groupNames]
  return isNotNullish(group.user_group_id)
    ? getGroupNames(groupKeyValue, group.user_group_id, newGroupNames, visitedIds)
    : newGroupNames
}
